import { VideoInfo } from 'clarifai-web-grpc/proto/clarifai/api/resources_pb';
import { UiRegionItem } from 'utils/cfUiItems/base';
import { makeUID } from 'utils/strings/strings';
import { getShapeProps } from 'utils/predictions/predictions';
import { getBase64ImageSecurily } from 'utils/inputs';
import { AnnoUiFrameItem } from 'utils/cfUiItems/annotations';
import { TIAnnotationMST } from 'modules/Annotations/store/annotation';
import { VideoMask } from 'modules/Annotations/helpers/storeHelpers';

export const DEFAULT_FPS = 30;

export const formatTime = (seconds: number): string => {
  /// TODO(alexandros): Make this support hours for longer clips ;)
  let sec = Math.floor(seconds);
  const mins = sec / 60;
  sec %= 60;

  const minPrefix = mins < 10 ? '0' : '';
  const secPrefix = sec < 10 ? '0' : '';

  const fmt = `${minPrefix}${Math.floor(mins)}:${secPrefix}${Math.floor(sec)}`;
  return fmt;
};

export const extractVideoMetadata = (videoMetadata?: VideoInfo | CF.API.Metadata) => {
  if (videoMetadata instanceof VideoInfo) {
    return { fps: videoMetadata?.getFps() || DEFAULT_FPS, framesCount: videoMetadata?.getFrameCount() || 0 };
  } else {
    return { fps: videoMetadata?.fps || DEFAULT_FPS, framesCount: videoMetadata?.framesCount || 0 };
  }
};

export const hasMaskAnnotation = (region: AnnoUiFrameItem<TIAnnotationMST>): boolean => {
  const regionsData = (region?.data as CF.API.BaseRegionsData)?.regions;
  const firstRegion = regionsData?.[0];
  return Boolean(firstRegion?.region_info && Object.prototype.hasOwnProperty.call(firstRegion.region_info, 'mask'));
};

export const createUiRegionItem = (region: AnnoUiFrameItem<TIAnnotationMST>, firstRegion: CF.API.RegionsEntity): UiRegionItem => {
  return {
    key: makeUID(),
    regionData: getShapeProps({
      id: region.id,
      data: firstRegion.data,
      region_info: firstRegion.region_info,
      value: firstRegion.value,
    }),
    concepts: firstRegion.data?.concepts || [],
    metadata: region.metadata,
  };
};

export const getMaskUrl = (firstRegion: CF.API.RegionsEntity): string | undefined => {
  return (firstRegion.region_info as CF.API.ImageMaskRegionInfo)?.mask?.image?.url;
};

export const convertToBase64 = async (url: string): Promise<string> => {
  try {
    return await getBase64ImageSecurily(url);
  } catch (error) {
    console.error('Failed to convert mask URL to base64:', error);
    return url;
  }
};

export const getTrackConcept = (pointAnnotations: AnnoUiFrameItem<TIAnnotationMST>[]): CF.API.Concept => {
  const firstPointAnnotation = pointAnnotations[0];
  const firstRegion = (firstPointAnnotation?.data as CF.API.BaseRegionsData)?.regions?.[0];
  return (firstRegion?.data?.concepts?.[0] as CF.API.Concept) ?? ({} as CF.API.Concept);
};

export const extractPointAnnotations = (videoRegionList: AnnoUiFrameItem<TIAnnotationMST>[]): AnnoUiFrameItem<TIAnnotationMST>[] => {
  return videoRegionList.filter((region) => {
    const regionsData = (region?.data as CF.API.BaseRegionsData)?.regions;
    const firstRegion = regionsData?.[0];
    return Boolean(firstRegion?.region_info && Object.prototype.hasOwnProperty.call(firstRegion.region_info, 'point'));
  });
};

export const groupAnnotationsByFrame = (pointAnnotations: AnnoUiFrameItem<TIAnnotationMST>[]): Record<number, Array<UiRegionItem>> => {
  return pointAnnotations.reduce((frameMap, region) => {
    const frameIndex = region.frame_info.index;
    const regionsData = (region?.data as CF.API.BaseRegionsData)?.regions;
    const firstRegion = regionsData && regionsData[0];

    if (firstRegion) {
      const uiRegionItem = createUiRegionItem(region, firstRegion);

      if (!frameMap[frameIndex]) {
        frameMap[frameIndex] = [];
      }
      frameMap[frameIndex].push(uiRegionItem);
    }

    return frameMap;
  }, {} as Record<number, Array<UiRegionItem>>);
};

export const extractMaskUrlsByFrame = async (videoRegionList: AnnoUiFrameItem<TIAnnotationMST>[]): Promise<Record<string, string>> => {
  const maskUrlsByFrame: Record<string, string> = {};

  await Promise.all(
    videoRegionList.map(async (region) => {
      if (hasMaskAnnotation(region)) {
        const regionsData = (region?.data as CF.API.BaseRegionsData)?.regions;
        const firstRegion = regionsData && regionsData[0];
        const maskUrl = firstRegion ? getMaskUrl(firstRegion) : undefined;

        if (maskUrl) {
          const base64Url = await convertToBase64(maskUrl);
          maskUrlsByFrame[String(region.frame_info.index)] = base64Url;
        }
      }
    }),
  );

  return maskUrlsByFrame;
};

export const createVideoMaskData = (maskUrlsByFrame: Record<string, string>, pointAnnotations: AnnoUiFrameItem<TIAnnotationMST>[]): VideoMask => {
  return {
    frames: maskUrlsByFrame,
    track: getTrackConcept(pointAnnotations),
  };
};

export const processVideoAnnotations = async (
  videoRegionList: AnnoUiFrameItem<TIAnnotationMST>[],
): Promise<{
  pointAnnotationsByFrame: Record<number, Array<UiRegionItem>>;
  videoMasks: VideoMask[];
}> => {
  if (videoRegionList.length === 0) {
    return {
      pointAnnotationsByFrame: {},
      videoMasks: [],
    };
  }

  const pointAnnotations = extractPointAnnotations(videoRegionList);
  const pointAnnotationsByFrame = groupAnnotationsByFrame(pointAnnotations);

  const maskUrlsByFrame = await extractMaskUrlsByFrame(videoRegionList);

  let videoMasks: VideoMask[] = [];
  if (Object.keys(maskUrlsByFrame).length > 0) {
    const videoMaskData = createVideoMaskData(maskUrlsByFrame, pointAnnotations);
    videoMasks = [videoMaskData];
  }

  return {
    pointAnnotationsByFrame,
    videoMasks,
  };
};
